import React from "react";
import { Link } from "react-router-dom";
import {
  Linkedin,
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  MessageCircleMore,
} from "lucide-react";
import Container from "./Container";
import { cls } from "../utils/helpers";
import {
  COMPANY_NAME,
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_RAW,
  WHATSAPP_NUM,
  COMPANY_ADDRESS,
  LINKEDIN_URL,
} from "../utils/constants";

function openWhatsApp(preset) {
  const num = WHATSAPP_NUM.replace(/\D/g, "");
  const text = encodeURIComponent(
    preset ||
      "Bonjour, je souhaite échanger au sujet de vos prestations / software."
  );
  window.open(`https://wa.me/${num}?text=${text}`, "_blank");
}

export default function Footer({ p, theme, t }) {
  const linkCls = cls(
    "text-sm",
    theme === "dark"
      ? "text-blue-200 hover:text-white"
      : "text-gray-600 hover:text-indigo-800"
  );
  const titleCls = cls(
    "text-sm font-semibold uppercase tracking-wide",
    theme === "dark" ? "text-white" : "text-indigo-900"
  );
  const iconCls = theme === "dark" ? "text-blue-200" : "text-indigo-700";

  const cols = [
    {
      title: t("footer.col.presta"),
      items: [
        { label: t("presta.direction"), href: "/prestations#presta-direction" },
        { label: t("presta.pilotage"), href: "/prestations#presta-pilotage" },
        { label: t("presta.etudes"), href: "/prestations#presta-etudes" },
        { label: t("presta.suivi"), href: "/prestations#presta-suivi" },
        { label: t("presta.optim"), href: "/prestations#presta-optimisation" },
        { label: t("presta.log"), href: "/prestations#presta-logistique" },
      ],
    },
    {
      title: t("footer.col.software"),
      items: [
        { label: t("soft.plan.title"), href: "/software#soft-planification" },
        { label: t("soft.collab.title"), href: "/software#soft-collaboration" },
        { label: t("soft.log.title"), href: "/software#soft-logistique" },
        { label: t("soft.qual.title"), href: "/software#soft-qualite" },
      ],
    },
    {
      title: t("footer.col.company"),
      items: [
        { label: t("nav.references"), href: "/references" },
        { label: t("nav.resources"), href: "/ressources" },
        { label: t("nav.recruitment"), href: "/recrutement" },
        { label: t("nav.contact"), href: "/contact" },
      ],
    },
    {
      title: t("footer.col.legal"),
      items: [
        { label: t("footer.link.legal"), href: t("footer.link.legalLink") },
        { label: t("footer.link.terms_and_conditions_of_subscription"), href: t("footer.link.terms_and_conditions_of_subscription_link") },
        { label: t("footer.link.terms_and_conditions_of_use"), href: t("footer.link.terms_and_conditions_of_use_link") },
        { label: t("footer.link.privacy"), href: t("footer.link.privacy_link") },
        { label: t("footer.link.cookies"), href: "/cookies" },
        { label: t("footer.link.sitemap"), href: "/sitemap" },
      ],
    },
  ];

  return (
    <footer className={p.footer}>
      <Container>
        <div className="py-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className={cls("text-xl font-bold", theme === "dark" ? "text-white" : "text-indigo-900")}>
              {t("footer.value")}
            </h3>
            <p
              className={cls(
                "mt-1 text-sm",
                theme === "dark" ? "text-blue-200" : "text-gray-600"
              )}
            >
              {t("footer.value.sub")}
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/contact" className={p.ctaPrimary}>
              {t("nav.quote")}
            </Link>
          </div>
        </div>

        <div className="py-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {cols.map((c) => (
            <div key={c.title}>
              <div className={titleCls}>{c.title}</div>
              <ul className="mt-3 space-y-2">
                {c.items.map((it) => (
                  <li key={it.label}>
                    {it.href.startsWith('http') ? (
                      <a
                        href={it.href}
                        className={linkCls}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {it.label}
                      </a>
                    ) : (
                      <Link to={it.href} className={linkCls}>
                        {it.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="py-6 border-t"
          style={{ borderColor: theme === "dark" ? "#1f3b76" : "#e0e7ff" }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <a
                href={LINKEDIN_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className={cls(
                  "p-2 rounded-lg ring-1",
                  theme === "dark"
                    ? "ring-blue-700 hover:bg-blue-900"
                    : "ring-indigo-200 hover:bg-indigo-50"
                )}
              >
                <Linkedin className={iconCls} size={18} />
              </a>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className={cls("flex items-center gap-2", linkCls)}
              >
                <Mail size={16} /> {CONTACT_EMAIL}
              </a>
              <a
                href={`tel:${CONTACT_PHONE_RAW}`}
                className={cls("flex items-center gap-2", linkCls)}
              >
                <Phone size={16} /> {CONTACT_PHONE}
              </a>
              <button
                onClick={() => openWhatsApp()}
                className={cls("flex items-center gap-2", linkCls)}
              >
                <MessageCircleMore size={16} /> WhatsApp
              </button>
              <span
                className={cls(
                  "hidden sm:inline-flex items-center gap-2",
                  linkCls
                )}
              >
                <MapPin size={16} /> {COMPANY_ADDRESS}
              </span>
            </div>
            <div
              className={cls(
                "text-sm",
                theme === "dark" ? "text-blue-200" : "text-gray-500"
              )}
            >
              © {new Date().getFullYear()} {COMPANY_NAME} — {t("footer.copyright")}
            </div>
          </div>
        </div>

        <div className="py-4 flex justify-end">
          <Link
            to="/"
            className={cls(
              "inline-flex items-center gap-1 text-sm",
              theme === "dark"
                ? "text-blue-200 hover:text-white"
                : "text-indigo-700 hover:text-indigo-900"
            )}
          >
            {t("footer.back")} <ArrowUpRight size={16} />
          </Link>
        </div>
      </Container>
    </footer>
  );
}
